import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/toaster';

// Pages
import LoginPage from '@/pages/LoginPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import UploadPage from '@/pages/UploadPage';
import TweetViewerPage from '@/pages/TweetViewerPage';
import DashboardPage from '@/pages/DashboardPage';
import AdminPage from '@/pages/AdminPage';
import AccessDeniedPage from '@/pages/AccessDeniedPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send session token from localStorage for mobile browsers
axios.interceptors.request.use((config) => {
  // Don't add Authorization header to /auth/session endpoint (it doesn't need it)
  if (!config.url.includes('/auth/session')) {
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${sessionToken}`;
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Auth wrapper component
function AuthWrapper({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    handleAuth();
  }, []);

  const handleAuth = async () => {
    // Check existing session
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      
      // Check if user is approved
      if (!response.data.is_approved) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Check if user tried to login but not approved
    if (user && !user.is_approved) {
      return <AccessDeniedPage user={user} />;
    }
    return <LoginPage />;
  }

  return children;
}

function AppContent() {
  const [hasTweets, setHasTweets] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkTweets();
  }, []);

  const checkTweets = async () => {
    try {
      const response = await axios.get(`${API}/analytics/summary`, {
        withCredentials: true
      });
      setHasTweets(response.data.total_tweets > 0);
    } catch (error) {
      console.error('Error checking tweets:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={hasTweets ? <Navigate to="/viewer" /> : <UploadPage onUploadSuccess={() => setHasTweets(true)} />} />
      <Route path="/upload" element={<UploadPage onUploadSuccess={() => setHasTweets(true)} />} />
      <Route path="/viewer" element={<TweetViewerPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/admin" element={<AdminPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes - no auth required */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        
        {/* Protected routes - require auth */}
        <Route path="/*" element={
          <AuthWrapper>
            <AppContent />
          </AuthWrapper>
        } />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

export default App;